import { useState, useEffect } from 'react';
import { getCreatorDashboard } from '../services/CreatorService';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { EmptyState } from '../components/EmptyState';
import { StatusBadge } from '../components/StatusBadge';
import { AccountPageHeader } from '../components/AccountPageHeader';
import { StarFill } from 'react-bootstrap-icons';

function CreatorDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const result = await getCreatorDashboard();
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="cd-error">Failed to load creator dashboard.</p>;
  if (!data) return null;

  const { creatorCode, isEnabled, totalPending, totalPaidOut, transactions, topSupporters } = data;

  return (
    <div className="cd-page">
      <AccountPageHeader
        icon={<StarFill />}
        title="Creator Dashboard"
        description="Track your creator code performance, supporters, and payouts"
        actions={
          <div className="cd-status-row">
            <span className="cd-code-display">{creatorCode.toUpperCase()}</span>
            {isEnabled
              ? <span className="cd-badge cd-badge--active">Active</span>
              : <span className="cd-badge cd-badge--disabled">Disabled</span>}
          </div>
        }
      />

      <div className="cd-totals-row">
        <div className="cd-total-card">
          <span className="cd-total-label">Pending Payout</span>
          <span className="cd-total-value cd-total-value--pending">${totalPending.toFixed(2)}</span>
        </div>
        <div className="cd-total-card">
          <span className="cd-total-label">Total Paid Out</span>
          <span className="cd-total-value cd-total-value--paid">${totalPaidOut.toFixed(2)}</span>
        </div>
      </div>

      <section className="cd-section">
        <h3 className="cd-section-title">Top Supporters</h3>
        {topSupporters.length === 0
          ? <EmptyState>No transactions yet.</EmptyState>
          : (
            <div className="cd-supporters-list">
              {topSupporters.map((s, i) => (
                <div key={s.characterName} className="cd-supporter-row">
                  <span className="cd-supporter-rank">#{i + 1}</span>
                  <span className="cd-supporter-name">{s.characterName}</span>
                  <span className="cd-supporter-count">{s.usageCount} {s.usageCount === 1 ? 'purchase' : 'purchases'}</span>
                </div>
              ))}
            </div>
          )
        }
      </section>

      <section className="cd-section">
        <h3 className="cd-section-title">Transactions</h3>
        {transactions.length === 0
          ? <EmptyState>No transactions yet.</EmptyState>
          : (
            <div className="cd-tx-table-wrap">
              <table className="cd-tx-table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Coins</th>
                    <th>Payout</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, i) => (
                    <tr key={i}>
                      <td>{tx.payerCharacterName}</td>
                      <td>{tx.coinsBought}</td>
                      <td>${tx.payoutAmount.toFixed(4)}</td>
                      <td><StatusBadge status={tx.isPaidOut ? 'paid_out' : 'pending'} /></td>
                      <td>{new Date(tx.transactionDate).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </section>
    </div>
  );
}

export { CreatorDashboard };
